import { useEffect, useRef, useState } from "react";
import { useAuth } from "../utils/auth";
import { sdkWrapperURL } from "../utils/api-url-list";
import Logo from "../assets/images/careplix-logo.png";

const Login = () => {
  const { login } = useAuth();
  const [isOTPSent, setOTPSent] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const otpTimerRef = useRef(0);
  const [otp, setOTP] = useState("");
  const [otpError, setOTPError] = useState("");
  const [otpTimer, setOTPTimer] = useState(0);

  const sendOTP = async () => {
    try {
      setLoading(true);
      setPhoneNumberError("");
      if (!/^\d{10}$/.test(phoneNumber)) {
        setPhoneNumber(phoneNumber.trim());
        setPhoneNumberError("Please enter a valid phone number.");
      } else {
        const resp = await fetch(sdkWrapperURL("/auth/send-otp"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "send",
            channel_id: "login_mobile",
            phone_no: phoneNumber,
          }),
        });
        const resp_json = await resp.json();
        if (resp_json.statusCode?.toString().startsWith("2")) {
          setOTPTimer(120);
          clearInterval(otpTimerRef.current);
          otpTimerRef.current = setInterval(
            () => setOTPTimer((t) => t - 1),
            1000
          );
          setOTP("");
          setOTPSent(true);
        } else throw new Error(resp_json?.message ?? "Failed to Send OTP");
      }
    } catch (err) {
      cancelOTP();
      setPhoneNumberError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    try {
      setLoading(true);
      setOTPError("");
      if (!/^\d{6}$/.test(otp)) {
        setOTP(otp.trim());
        setOTPError("Incorrect OTP provided. Please try again");
      } else {
        const resp = await fetch(sdkWrapperURL("/auth/sign-in"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            login_type: "auth",
            login_method: "otp_mobile",
            phone_no: phoneNumber,
            otp: otp,
          }),
        });
        const resp_json = await resp.json();
        if (resp_json?.statusCode?.toString().startsWith("2"))
          await login(resp_json.user.id_token, resp_json.user.refresh_token);
        else throw new Error(resp_json?.message ?? "Failed to Verify OTP");
      }
    } catch (err) {
      setOTPError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelOTP = (goback = true) => {
    setOTPTimer(0);
    clearInterval(otpTimerRef.current);
    setOTP("");
    setOTPError("");
    if (goback) setOTPSent(false);
  };

  useEffect(() => {
    if (otpTimer <= 0) cancelOTP(false);
  }, [otpTimer]);

  return (
    <main className="relative h-screen bg-[url(assets/backgrounds/login.svg)] bg-cover bg-center">
      <img
        className="absolute top-[2vh] left-[2.5vw] h-16"
        src={Logo}
        alt="CarePlix"
      />
      <h1 className="absolute top-[15vh] 3xl:top-[20vh] left-[10vw] text-primary text-3xl leading-tight font-bold drop-shadow-sm">
        Navigate Success with
        <br />
        Comprehensive Insights with Our
        <br />
        Robust Analytics Dashboard
      </h1>
      <div className="absolute top-[15vh] 3xl:top-[20vh] right-[10vw] w-[400px]">
        {isOTPSent ? (
          <>
            <h3 className="text-primary text-3xl font-semibold drop-shadow-sm">
              Verify OTP
            </h3>
            <p className="mt-4 text-darkgray text-base drop-shadow-sm">
              Enter the 6-digits OTP sent to your Phone Number
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!isLoading) {
                  if (otpTimer > 0) verifyOTP();
                  else sendOTP();
                }
              }}
            >
              <input
                id="otp"
                name="otp"
                className="block w-full mt-6 px-4 py-3 rounded-lg outline-none border border-lightgray/50 focus:border-secondary bg-white text-primary placeholder:text-lightgray text-sm"
                placeholder={otpTimer > 0 ? "Enter OTP" : "Timed Out"}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                onChange={(e) =>
                  e.target.value.length <= 6 && setOTP(e.target.value)
                }
                value={otp}
                disabled={isLoading || otpTimer <= 0}
              />
              {otpError.length > 0 && (
                <p className="mt-1.5 mx-1.5 text-xs text-error">{otpError}</p>
              )}
              {otpTimer > 0 && (
                <p className="mt-1.5 text-xs text-error text-right">
                  Resend OTP in {Math.floor(otpTimer / 60)}:
                  {(otpTimer % 60).toString().padStart(2, "0")} secs
                </p>
              )}
              <button
                className="block w-full mt-6 px-4 py-3 rounded-full outline-none bg-primary disabled:bg-darkgray text-white text-sm font-medium"
                type="submit"
                disabled={isLoading}
              >
                {otpTimer > 0 ? "Verify OTP" : "Resend OTP"}
              </button>
              <button
                className="block mx-auto mt-6 outline-none text-secondary text-sm font-medium"
                type="button"
                onClick={cancelOTP}
                disabled={isLoading}
              >
                Cancel
              </button>
            </form>
          </>
        ) : (
          <>
            <h3 className="text-primary text-3xl font-semibold drop-shadow-sm">
              Log In
            </h3>
            <p className="mt-4 text-darkgray text-base drop-shadow-sm">
              Login by simply verifying your Phone Number
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!isLoading) sendOTP();
              }}
            >
              <input
                id="phone"
                name="phone"
                className="block w-full mt-6 px-4 py-3 rounded-lg outline-none border border-lightgray/50 focus:border-secondary bg-white text-primary placeholder:text-lightgray text-sm"
                placeholder="Enter Phone Number"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                autoFocus
                maxLength={10}
                onChange={(e) =>
                  e.target.value.length <= 10 && setPhoneNumber(e.target.value)
                }
                value={phoneNumber}
                disabled={isLoading}
              />
              {phoneNumberError.length > 0 && (
                <p className="mt-1.5 mx-1.5 text-xs text-error">
                  {phoneNumberError}
                </p>
              )}
              <button
                className="block w-full mt-6 px-4 py-3 rounded-full outline-none bg-primary disabled:bg-darkgray text-white text-sm font-medium"
                type="submit"
                disabled={isLoading}
              >
                Send OTP
              </button>
            </form>
          </>
        )}
      </div>
      <footer className="fixed inset-x-0 bottom-0 px-12 py-6 flex items-center justify-between gap-5 bg-primary text-white text-base">
        <div className="shrink-0">
          <p className="font-light">For Queries Reach out to us at:</p>
          <a href="mailto:connect@careplix.com" className="font-medium">
            connect@careplix.com
          </a>
        </div>
        <p className="text-white">
          All rights reserved &copy; CarePlix | CareNow Healthcare Pvt Ltd&nbsp;
          {new Date().getFullYear()}
        </p>
      </footer>
    </main>
  );
};

export default Login;
